import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WRAPPED_V2_PATH } from '@/constants/paths';
import EventTracker from '@/utils/EventTracker';

const ANIMATION_NAME = 'viomeWrappedBob';
const MID_EMOJI_CLASS = 'viome-wrapped-mid-emoji';
const BOB_KEYFRAMES_ID = 'viome-wrapped-bob-kf';
const BOB_KEYFRAMES =
  `@keyframes ${ANIMATION_NAME} { 0%,100% { transform: translateY(0) rotate(var(--rot,0deg)); } 50% { transform: translateY(-4px) rotate(var(--rot,0deg)); } } ` +
  `.${MID_EMOJI_CLASS} { display: none; } ` +
  `@media (min-width: 600px) { .${MID_EMOJI_CLASS} { display: block; } } ` +
  `@media (prefers-reduced-motion: reduce) { [style*="${ANIMATION_NAME}"] { animation: none !important; } }`;

const PULSE_ANIMATION_NAME = 'viomeWrappedPulse';
const PULSE_KEYFRAMES = `@keyframes ${PULSE_ANIMATION_NAME} { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(2.5); opacity: 0; } }`;

const HEADLINE_STYLE: React.CSSProperties = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 40,
  fontWeight: 700,
  letterSpacing: '-0.04em',
  lineHeight: 0.95,
};

// One impression per app load, even if the card mounts on both HomePage and ResultsReady.
let impressionLogged = false;

function useBobKeyframes() {
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(BOB_KEYFRAMES_ID)) return;
    const s = document.createElement('style');
    s.id = BOB_KEYFRAMES_ID;
    s.textContent = BOB_KEYFRAMES;
    document.head.appendChild(s);
  }, []);
}

function ViomeWordmark({
  height = 13,
  color = '#fff',
}: {
  height?: number;
  color?: string;
}) {
  const width = height * (110 / 18);
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 110 18"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
      aria-label="Viome"
    >
      <path d="M2 4 L9 16 L16 4" />
      <line x1="9" y1="0.5" x2="9" y2="2.5" />
      <line x1="24" y1="4" x2="24" y2="16" />
      <circle cx="39" cy="10" r="6" />
      <path d="M54 16 L54 4 L62 14 L70 4 L70 16" />
      <path d="M86 4 L78 4 L78 16 L86 16 M78 10 L84 10" />
    </svg>
  );
}

type FloatEmojiProps = {
  right: number | string;
  top: number;
  size?: number;
  emoji: string;
  rotate?: number;
  delay?: number;
  midOnly?: boolean;
};

function FloatEmoji({
  right,
  top,
  size = 28,
  emoji,
  rotate = 0,
  delay = 0,
  midOnly = false,
}: FloatEmojiProps) {
  return (
    <div
      aria-hidden
      className={midOnly ? MID_EMOJI_CLASS : undefined}
      style={
        {
          position: 'absolute',
          right,
          top,
          fontSize: size,
          lineHeight: 1,
          ['--rot' as string]: `${rotate}deg`,
          animation: `${ANIMATION_NAME} 4.5s ease-in-out infinite ${delay}s`,
          willChange: 'transform',
          textShadow: '0 2px 6px rgba(0,0,0,0.25)',
          pointerEvents: 'none',
        } as React.CSSProperties
      }
    >
      {emoji}
    </div>
  );
}

// Even 4% horizontal step. midOnly entries only render at >=600px viewport.
const FLOATS: FloatEmojiProps[] = [
  { right: '4%', top: 105, size: 26, emoji: '🍅', rotate: 8, delay: 1.8 },
  { right: '8%', top: 170, size: 20, emoji: '🍇', rotate: 6, delay: 0.3 },
  { right: '12%', top: 48, size: 22, emoji: '🥝', rotate: 12, delay: 0.6 },
  { right: '16%', top: 140, size: 22, emoji: '🥦', rotate: -12, delay: 2.4 },
  { right: '20%', top: 18, size: 26, emoji: '🍊', rotate: -8, delay: 0 },
  { right: '24%', top: 180, size: 22, emoji: '🍉', rotate: -6, delay: 1.5 },
  { right: '28%', top: 68, size: 24, emoji: '🌶️', rotate: -15, delay: 1.2 },
  {
    right: '32%',
    top: 142,
    size: 22,
    emoji: '🥕',
    rotate: 8,
    delay: 2.1,
    midOnly: true,
  },
  {
    right: '36%',
    top: 28,
    size: 22,
    emoji: '🫐',
    rotate: 14,
    delay: 0.9,
    midOnly: true,
  },
  {
    right: '40%',
    top: 88,
    size: 24,
    emoji: '🥑',
    rotate: -6,
    delay: 1.4,
    midOnly: true,
  },
  {
    right: '44%',
    top: 184,
    size: 20,
    emoji: '🍓',
    rotate: 5,
    delay: 2.6,
    midOnly: true,
  },
  {
    right: '48%',
    top: 52,
    size: 20,
    emoji: '🍋',
    rotate: -10,
    delay: 0.5,
    midOnly: true,
  },
  {
    right: '52%',
    top: 120,
    size: 22,
    emoji: '🌽',
    rotate: -14,
    delay: 1.7,
    midOnly: true,
  },
];

export const WrappedCard: React.FC<{ isNewlyReleased?: boolean }> = ({ isNewlyReleased = false }) => {
  const navigate = useNavigate();
  useBobKeyframes();

  React.useEffect(() => {
    if (!isNewlyReleased) return;
    if (typeof document === 'undefined') return;
    if (document.getElementById('viome-wrapped-pulse-kf')) return;
    const s = document.createElement('style');
    s.id = 'viome-wrapped-pulse-kf';
    s.textContent = PULSE_KEYFRAMES;
    document.head.appendChild(s);
  }, [isNewlyReleased]);

  React.useEffect(() => {
    if (impressionLogged) return;
    impressionLogged = true;
    EventTracker.trackEvent('home', 'home_wrapped_card_seen');
  }, []);

  const handleOpen = () => {
    EventTracker.trackEvent('home', 'home_wrapped_card_clicked');
    navigate(WRAPPED_V2_PATH);
  };

  return (
    <div className="w-full" data-testid="home-wrappedCard">
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Open The Playbook"
        style={{
          all: 'unset',
          position: 'relative',
          display: 'block',
          width: '100%',
          height: 224,
          borderRadius: 20,
          overflow: 'hidden',
          cursor: 'pointer',
          fontFamily: 'Inter, system-ui, sans-serif',
          background: `
            radial-gradient(ellipse 90% 70% at 30% 50%, #2D6B6B 0%, transparent 70%),
            radial-gradient(ellipse 80% 60% at 80% 60%, #3F7D6E 0%, transparent 60%),
            linear-gradient(135deg, #1F4855 0%, #2D5C4A 60%, #1F4855 100%)
          `,
          boxShadow: '0 10px 30px rgba(31,72,85,0.25)',
          textAlign: 'left',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 358 224"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.35,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          <path
            d="M 30 40 Q 120 10, 220 50 T 360 80"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="1"
            fill="none"
            strokeDasharray="3 5"
          />
        </svg>

        {FLOATS.map((f) => (
          <FloatEmoji key={f.emoji} {...f} />
        ))}

        <div
          style={{
            position: 'relative',
            zIndex: 10,
            height: '100%',
            padding: '22px 22px 18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            maxWidth: '70%',
          }}
        >
          <div>
            {isNewlyReleased ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(0,0,0,0.35)',
                  borderRadius: 999,
                  padding: '5px 10px',
                  marginBottom: 10,
                }}
              >
                <div style={{ position: 'relative', width: 7, height: 7, flexShrink: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: '#F7CB4D',
                      animation: `${PULSE_ANIMATION_NAME} 1.4s ease-out infinite`,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: '#F7CB4D',
                    }}
                  />
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                  Your results are ready
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <ViomeWordmark />
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: '#1F4855',
                    padding: '2px 7px',
                    borderRadius: 999,
                    letterSpacing: '0.14em',
                    whiteSpace: 'nowrap',
                    background: '#BCCC54',
                  }}
                >
                  FULL BODY INTELLIGENCE
                </span>
              </div>
            )}

            {isNewlyReleased && <div style={{ height: 12 }} />}
            <div
              data-testid="home-wrappedCard-title"
              style={{ ...HEADLINE_STYLE, color: '#FFFFFF' }}
            >
              The
            </div>
            <div style={{ ...HEADLINE_STYLE, marginTop: 2, color: '#FFFFFF' }}>
              Playbook
            </div>
          </div>

          <div>
            {!isNewlyReleased && (
              <div
                data-testid="home-wrappedCard-description"
                style={{
                  fontSize: 11.5,
                  color: 'rgba(255,255,255,0.78)',
                  lineHeight: 1.4,
                  marginBottom: 10,
                  maxWidth: 220,
                }}
              >
                Your biology, distilled.
              </div>
            )}
            <span
              data-testid="home-wrappedCard-cta"
              style={{
                background: '#F4C95E',
                color: '#1F4855',
                padding: '9px 16px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '-0.005em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 14px rgba(244,201,94,0.35)',
              }}
            >
              Start your story <span style={{ fontSize: 14 }}>→</span>
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};
